import type { Collaborator } from '@stratumn/studio-shared';
import { CheckIcon, User } from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DEFAULT_AVATAR } from '@/utils/constants';
import { FilterPill } from './FilterPill';
import { type FilterOperator, isMultiOperator } from './types';

type UserFilterPickerProps = {
  collaborators: Collaborator[];
  selectedIds: string[];
  operator: FilterOperator;
  onChange: (ids: string[], operator: FilterOperator) => void;
  onRemove: () => void;
  /** When true, the value popover opens automatically on mount */
  autoOpen?: boolean;
};

export const UserFilterPicker = ({
  collaborators,
  selectedIds,
  operator,
  onChange,
  onRemove,
  autoOpen = false
}: UserFilterPickerProps) => {
  const [open, setOpen] = useState(autoOpen);
  const isMulti = isMultiOperator(operator);

  const handleSelect = (id: string) => {
    if (!isMulti) {
      onChange([id], operator);
      setOpen(false);
      return;
    }

    if (selectedIds.includes(id)) {
      onChange(
        selectedIds.filter(v => v !== id),
        operator
      );
    } else {
      onChange([...selectedIds, id], operator);
    }
  };

  const handleOperatorChange = (newOp: FilterOperator) => {
    if (!isMultiOperator(newOp) && selectedIds.length > 1) {
      onChange([selectedIds.at(-1)], newOp);
    } else {
      onChange(selectedIds, newOp);
    }
  };

  const selectedCollaborators = collaborators.filter(c =>
    selectedIds.includes(c.id)
  );

  const renderValueSummary = () => {
    if (selectedCollaborators.length === 0) {
      return <span className="text-muted-foreground">Select...</span>;
    }

    if (selectedCollaborators.length === 1) {
      const user = selectedCollaborators[0];
      return (
        <div className="flex items-center gap-1.5">
          <Avatar className="size-4">
            <AvatarImage src={user.avatar ?? DEFAULT_AVATAR} />
          </Avatar>
          <span className="max-w-24 truncate">{user.name}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <Avatar className="size-4">
          <AvatarImage
            src={selectedCollaborators.at(-1).avatar ?? DEFAULT_AVATAR}
          />
        </Avatar>
        <span>{selectedCollaborators.length} users</span>
      </div>
    );
  };

  return (
    <FilterPill
      icon={<User className="size-3.5" />}
      label="User"
      onOperatorChange={handleOperatorChange}
      onRemove={onRemove}
      operator={operator}
    >
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button
            className="flex h-full items-center gap-1.5 px-2 hover:bg-accent"
            type="button"
          >
            {renderValueSummary()}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <Command className="border-none shadow-none">
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {collaborators.map(collaborator => {
                  const isSelected = selectedIds.includes(collaborator.id);
                  return (
                    <CommandItem
                      className="cursor-pointer"
                      key={collaborator.id}
                      onSelect={() => handleSelect(collaborator.id)}
                    >
                      {isMulti && (
                        <div
                          className={cn(
                            'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : '[&_svg]:invisible'
                          )}
                        >
                          <CheckIcon className="size-3" />
                        </div>
                      )}
                      <Avatar className="mr-2 size-5">
                        <AvatarImage
                          src={collaborator.avatar ?? DEFAULT_AVATAR}
                        />
                      </Avatar>
                      <span className="truncate">{collaborator.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FilterPill>
  );
};
